/* ---------------------------------------------------------------------------
   Rendering the few values whose written form is a jurisdiction's own.

   Two of them: a position along a road, and a sum of money. Everything else this
   application prints — a length in metres, a width, a crack in millimetres, a
   date — is written the same way in Kathmandu and in Quezon City, and putting it
   through a tenant-aware formatter would be indirection with no difference at
   the end of it.

   THE CHAINAGE ONE MATTERS MORE THAN IT LOOKS. `Bridge.km` is one number, and
   the two offices read it off different paper: DoR's drawings are marked `km
   12.340` and DPWH's are marked `796+180`. An inspector who reads a position in
   a notation their own plans do not use is an inspector who transcribes it
   wrong, and a transcription error in a chainage puts a defect at the wrong
   pier. So the register stores the number once and each tenant reads it its own
   way.

   WHERE A RECORDED STATION EXISTS, PREFER IT. DPWH stations are not always
   normalised — `848+1066` is 1066 m past kilometre 848, and `849+066` is the
   same point written conventionally. Rendering the number back gives the second,
   which silently corrects a figure somebody has on a drawing. `stationOf` in
   this file therefore takes the recorded string when the record carries one and
   only computes when it does not. See the `station` extra in
   src/tenant/profiles/ph-dpwh.ts.
   --------------------------------------------------------------------------- */

import type { Bridge } from '../data/types.ts';
import { active } from './registry.ts';
import type { ChainageStyle, Currency } from './types.ts';

/**
 * A position along a road, written the way the tenant's drawings write it.
 *
 * `km` gives `km 12.340` — three decimal places, so a metre is visible; a
 * chainage quoted to one decimal is a chainage quoted to a hundred metres, which
 * is not a position on a bridge.
 *
 * `station` gives `796+180` — whole kilometres, a plus, then metres padded to
 * three digits. The pad is not cosmetic: `796+18` would read as 18 m where
 * `796+180` means 180 m, and the two are different ends of the deck.
 */
export function formatChainage(km: number | null | undefined, style: ChainageStyle): string | null {
  if (km == null || !Number.isFinite(km)) return null;
  if (style === 'station') {
    const whole = Math.floor(km);
    /* Rounded, not truncated: at 796.1799999 floor would give 179 m and lose a
       metre to a float that was only ever meant to be 180. */
    const metres = Math.round((km - whole) * 1000);
    /* A round-up that carries past 1000 m belongs to the next kilometre, or the
       station reads `796+1000`, which is not a station. */
    return metres === 1000 ? `${whole + 1}+000` : `${whole}+${String(metres).padStart(3, '0')}`;
  }
  return `km ${km.toFixed(3)}`;
}

/** The same, for the tenant in force. What the screens call. */
export function chainage(km: number | null | undefined): string | null {
  return formatChainage(km, active().chainage);
}

/**
 * One structure's position, preferring the figure it was given.
 *
 * A record whose `station` extra carries the string its source list printed
 * shows that string; anything else is computed from `km`. Null where the record
 * has neither, which the caller shows as *not recorded* rather than as a zero.
 */
export function stationOf(b: Bridge): string | null {
  const recorded = b.tenantFields.station;
  if (typeof recorded === 'string' && recorded.trim() !== '') return recorded.trim();
  return chainage(b.km);
}

/**
 * A sum of money, in the tenant's currency, with no conversion anywhere.
 *
 * Grouped in thousands and given no decimal places. Estimates in both offices
 * are prepared and approved to the whole unit — a bill of quantities that quotes
 * centavos on a bridge repair is quoting a precision the rate behind it does not
 * have — and a total that reads `Rs 1,240,000` is a total somebody can check
 * against a printed estimate at a glance.
 */
export function formatMoney(amount: number, currency: Currency = active().currency): string {
  if (!Number.isFinite(amount)) return '—';
  return `${currency.symbol} ${Math.round(amount).toLocaleString('en-US')}`;
}
